/**
 * `withStepUp` — wrap a `fetch`-style action so that a 401
 * `step_up_required` response automatically pops <StepUpAuthDialog/>,
 * waits for the user to re-prove identity, then retries the original
 * action exactly once.
 *
 * Usage
 *   await withStepUp(() => fetch("/api/users/me/change-password", {...}));
 *
 * Why a wrapper instead of putting this in apiFetch:
 *   - Most API calls are NOT sensitive; we don't want to pay the event-
 *     listener / dialog roundtrip cost on the hot read paths.
 *   - The caller usually wants to surface its own error UI when the
 *     user cancels, so a `step_up_cancelled` rejection bubbling up
 *     unchanged keeps that simple.
 */
import { STEP_UP_EVENT, type StepUpEventDetail } from "./step-up-event";
import { isSensitiveAction, type SensitiveAction } from "@workspace/api-zod";

export class StepUpCancelledError extends Error {
  constructor() {
    super("step_up_cancelled");
    this.name = "StepUpCancelledError";
  }
}

function dispatchStepUp(action: SensitiveAction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const detail: StepUpEventDetail = { action, resolve, reject };
    window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, { detail }));
  });
}

export async function withStepUp<T>(run: () => Promise<Response>): Promise<Response> {
  const res = await run();
  if (res.status !== 401) return res;
  // Clone before reading the body — the caller may want to read it on
  // the retry path if we end up returning this same response.
  const cloned = res.clone();
  let body: { error?: string; action?: string } = {};
  try {
    body = await cloned.json();
  } catch {
    return res;
  }
  if (body.error !== "step_up_required") return res;
  const action = body.action;
  if (!action || !isSensitiveAction(action)) return res;
  try {
    await dispatchStepUp(action);
  } catch (err) {
    // Cancel / dialog-rejection → bubble a typed error so the form can
    // distinguish "the user said no" from "the server said no".
    if (err instanceof Error && err.message === "step_up_cancelled") {
      throw new StepUpCancelledError();
    }
    throw err;
  }
  // Retry once — no second retry, that would loop on a stale stamp.
  return run();
}
